import type { UseControllableStateParams } from './index.types';
import { useCallback, useState } from 'react';

export type { UseControllableStateParams } from './index.types';

/**
 * Bridges the controlled / uncontrolled component patterns.
 *
 * - If `value` is provided, state is owned by the parent (controlled).
 * - Otherwise the hook owns the state internally (uncontrolled), while still
 *   forwarding every change to `onChange`.
 */
export function useControllableState<T>({
  value,
  defaultValue,
  onChange,
}: UseControllableStateParams<T>): readonly [T, (next: T) => void] {
  const [uncontrolled, setUncontrolled] = useState<T>(defaultValue);
  const isControlled = value !== undefined;
  const state = isControlled ? value : uncontrolled;

  const setState = useCallback(
    (next: T) => {
      if (!isControlled) {
        setUncontrolled(next);
      }

      onChange?.(next);
    },
    [isControlled, onChange]
  );

  return [state, setState] as const;
}
